import { Radio, Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { useEffect, useRef } from 'react';
import { ImapTariffsList } from 'reactApp/appHelpers/featuresHelpers/features/ImapSubscription';
import type { Product } from 'reactApp/types/Billing';
import { TariffLabel } from 'reactApp/ui/TariffCardNew/components/TariffLabel';
import { ETariffLabelKind, ETariffLabelRadius } from 'reactApp/ui/TariffCardNew/components/TariffLabel.types';
import { useButtonAdditionalText } from 'reactApp/ui/TariffSectionNew/hooks/useButtonAdditionalText';
import { useSwitchProducts } from 'reactApp/ui/TariffSectionNew/hooks/useSwitchProducts';
import { useTariffLabel } from 'reactApp/ui/TariffSectionNew/hooks/useTariffLabel';
import type { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { useProductPrice } from 'reactApp/ui/TariffsModal/TariffListCard/hooks/useProductPrice';
import type { TariffByModalTab } from 'reactApp/ui/TariffsModal/TariffsModalContent.types';
import { isTransactPeriod } from 'reactApp/utils/Period';

import styles from './TariffCardMobile.css';

interface TariffCardMobileProps {
    tariff: TariffByModalTab;
    activeTab: ETabsName;
    setTariff: (tariff: TariffByModalTab) => void;
    setProduct: (product: Product) => void;
    onRowClick: (tariff: TariffByModalTab, product: Product) => void;
    index: number;
    groupName: string;
    hasRecommended?: boolean;
    dataQa?: string;
}

const getTitle = (tariff: TariffByModalTab, product: Product) => {
    const { title } = tariff;
    const id = product.id;

    if (title) {
        return <>{title}</>;
    }

    if (ImapTariffsList.includes(id)) {
        return <>Пароли для внешних приложений</>;
    }

    return <>+{product.space.value}</>;
};

export const TariffCardMobile = ({
    tariff,
    activeTab,
    setTariff,
    setProduct,
    index,
    groupName,
    hasRecommended,
    onRowClick,
    dataQa,
}: TariffCardMobileProps) => {
    const radioRef = useRef<HTMLInputElement>(null);
    const { isRecommended, products, tariffId, label: providedLabel, labelKind: providedLabelKind } = tariff;
    const { product } = useSwitchProducts({ products, activeTab });

    const { discount, priceText, transactText } = useProductPrice({ product });
    const additionalText = useButtonAdditionalText(product);

    const mainText = isTransactPeriod(product.period) ? transactText : priceText;

    const subtitle = (
        <>
            <span data-qa-id={dataQa && `${dataQa}-subtitle`}>{mainText}</span>
            {additionalText && <span data-qa-id={dataQa && `${dataQa}-subtitle-add`}> {additionalText}</span>}
        </>
    );

    const [showLabel, label, labelKind] = useTariffLabel({
        product,
        skipDefaultLabel: true,
        showDiscount: false,
        isRecommended: isRecommended && !discount,
        primaryLabel: isRecommended ? 'Выгодно' : '',
        isRebranding: true,
        providedLabel,
        providedLabelKind,
    });

    const onClick = () => onRowClick(tariff, product);

    useEffect(() => {
        let isChecked = true;
        if (hasRecommended && isRecommended) {
            isChecked = true;
        }
        if (!hasRecommended && index === 1) {
            isChecked = true;
        }

        if (isChecked && radioRef.current) {
            radioRef.current.checked = true;
            setTariff(tariff);
            setProduct(product);
        }
    }, []);

    useEffect(() => {
        if (radioRef.current?.checked) {
            setProduct(product);
        }
    }, [activeTab]);

    return (
        <Radio
            name={groupName}
            key={index}
            onClick={onClick}
            getRef={radioRef}
            value={tariffId}
            className={classNames(styles.tariffCard, {
                [styles.border_active]: radioRef.current?.checked,
            })}
        >
            <Text className={styles.title} data-qa-id={dataQa && `${dataQa}-title`}>
                {getTitle(tariff, product)}
            </Text>
            <Spacing size={4} />
            <Text className={styles.subtitle}>{subtitle}</Text>
            {showLabel && labelKind !== ETariffLabelKind.bottomCenter && (
                <TariffLabel
                    className={classNames(styles.label)}
                    radius={ETariffLabelRadius.r16}
                    kind={labelKind}
                    internalClassname={classNames(styles.labelInternal, {
                        [styles.labelInternal_active]: radioRef.current?.checked,
                    })}
                >
                    {label}
                </TariffLabel>
            )}
        </Radio>
    );
};
